/**
 * @file Project.js.
 * @author Sérgio Ferraz Souza <todentro@gmail.com>
 * @copyright Cognatis GeoAnalytics & Big Data Ltda :: 2020
 */

import Request from '../components/http/request'
import Service from './Service'

const defaultJsonHeaders = {
  headers: {
    'Content-Type': 'application/json'
  }
}

/**
 * Project Management Service
 * @class
 */
class Project extends Service {
  constructor (options, resource) {
    super(options)
    this.resource = resource
     //this.request = new Request({environment: 'local', tag: 'das'})
    this.request = new Request({tag: 'das'})
  }

  /**
   * Add layer to project
   *
   * @async
   * @function addLayer
   * @param {object} model - Model of the metadata
   * @return {Promise<json>} The json with layer created
   */
  async addLayer (model) {
    return new Promise((resolve, reject) => {
      if (typeof model === 'undefined') reject(new Error('Esperado o modelo do layer'))
      let endPoint = `/api/0.1-a/Project/layer`

      let data = {
        'ProjectId': model.projectId,
        'LayerId': model.layerId,
        'Position': model.position || 0
      }
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Associates the project with the user
   *
   * @async
   * @function associateToUser
   * @param {object} model - Model of the metadata
   * @return {Promise<json>} The json with association created
   */
  async associateToUser (model) {
    return new Promise((resolve, reject) => {
      if (typeof model === 'undefined') reject(new Error('Esperado o modelo do projeto'))
      let endPoint = `/api/0.1-a/Project/user`

      this.request.post(endPoint, model, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }
  /**
   * Change layer position
   *
   * @async
   * @function changeLayer
   * @param {object} model - Model of the metadata
   * @return {Promise<json>} The json with association updated
   */
  async changeLayer (model) {
    return new Promise((resolve, reject) => {
      if (typeof model === 'undefined') reject(new Error('Esperado o modelo do layer'))
      let endPoint = `/api/0.1-a/Project/layer`

      let data = {
        'ObjectId': model.objectid,
        'ProjectId': model.projectid,
        'LayerId': model.layerid,
        'Position': model.position || 0
      }
      this.request.patch(endPoint, data, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }
  /**
   * Change layer position
   *
   * @async
   * @function changeLayer
   * @param {object} model - Model of the metadata
   * @return {Promise<json>} The json with association updated
   */
  async changeLayers (listLayers) {
    if(listLayers.length===0){
      console.warn({title:'Esperado o modelo do layer', error: error})
      return
    }

    let listData = []
    for (let i = 0; i < listLayers.length; i++) {
      let model = listLayers[i]
      let data = {
        'ObjectId': model.objectid,
        'ProjectId': model.projectid,
        'LayerId': model.layerid,
        'Position': model.position || 0
      }
      listData.push(data)
    }
    return new Promise((resolve, reject) => {
      let endPoint = `/api/0.1-a/Project/layers`
      this.request.patch(endPoint, listData, defaultJsonHeaders)
          .then(response => resolve(response.data))
          .catch(() => reject)
    })
  }

  /**
   * Create project
   *
   * @async
   * @function create
   * @param {string} name - Project name
   * @param {string} description - Project description
   * @return {Promise<json>} The json with project created
   */
  async create (name, description = null) {
    return new Promise((resolve, reject) => {
      if (typeof name === 'undefined') reject(new Error('Esperado o nome do projeto'))
      let endPoint = `/api/0.1-a/Project`

      let data = {
        'Name': name,
        'Description': description
      }
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }
  /**
   * Create project options
   *
   * @async
   * @function createOptions
   * @param {object} options - Project options
   * @return {Promise<json>} The json with project options created
   */
  async createOptions (options = {}) {
    return new Promise((resolve, reject) => {
      if (typeof options === 'undefined') reject(new Error('Esperado o objeto options'))

      const endPoint = `/api/0.1-a/project/options`
      this.request.post(endPoint, options, defaultJsonHeaders)
        .then(res => resolve(res.data))
        .catch(() => reject)
    })
  }
  /**
   * Delete project
   *
   * @async
   * @function delete
   * @param {Number} projectId - Project Id.
   * @return {Promise<string>} The data from the URL.
   */
  async delete (projectId) {
    return new Promise((resolve, reject) => {
      if (typeof projectId === 'undefined') reject(new Error('Esperado o identificador do projeto'))
      let endPoint = `/api/0.1-a/Project/${projectId}`

      this.request.delete(endPoint, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }

  /**
   * Delete layer from project
   *
   * @async
   * @function deleteLayer
   * @param {Number} projectId - Project Id
   * @param {Number} layerId - Layer Id
   * @return {Promise<json>} The json with result
   */
  async deleteLayer (projectId, layerId) {
    return new Promise((resolve, reject) => {
      if (typeof projectId === 'undefined') reject(new Error('Esperado o identificador do projeto'))
      if (typeof layerId === 'undefined') reject(new Error('Esperado o identificador do layer'))

      let endPoint = `/api/0.1-a/Project/${projectId}/layers/${layerId}`
      this.request.delete(endPoint, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }

  /**
   * Get the active project
   *
   * @async
   * @function getActive
   * @param {Number} environment - Environment Id
   * @param {Number} user - User Id
   * @return {Promise<json>} The json with project
   */
  async getActive (environment, user) {
    return new Promise((resolve, reject) => {
      if (typeof environment === 'undefined') reject(new Error('Esperado o identificador do environment'))
      if (typeof user === 'undefined') reject(new Error('Esperado o identificador do usuário'))

      let endPoint = `/api/0.1-a/Project/active/${user}/${environment}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get the user projects
   *
   * @async
   * @function getByUser
   * @param {Number} environment - Environment Id
   * @param {Number} user - User Id
   * @return {Promise<json>} The json with the projects
   */
  async getByUser (environment, user) {
    return new Promise((resolve, reject) => {
      if (typeof environment === 'undefined') reject(new Error('Esperado o identificador do environment'))
      if (typeof user === 'undefined') reject(new Error('Esperado o identificador do usuário'))

      let endPoint = `/api/0.1-a/Project/users/${user}/${environment}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get project layers
   *
   * @async
   * @function getLayers
   * @param {Number} project - Project Id
   * @return {Promise<json>} The json with the layers
   */
  async getLayers (project) {
    return new Promise((resolve, reject) => {
      if (typeof project === 'undefined') reject(new Error('Esperado o identificador do projeto'))
      let endPoint = `/api/0.1-a/Project/${project}/layers`

      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }

  /**
   * Get project layers with DataViz
   *
   * @async
   * @function getLayers
   * @param {Number} project - Project Id
   * @return {Promise<json>} The json with the layers
   */
  async getLayersDataViz (project) {
    return new Promise((resolve, reject) => {
      if (typeof project === 'undefined') reject(new Error('Esperado o identificador do projeto'))
      const endPoint = '/api/0.1-a/ProxyPostgRest/api/rpc/getlayerbyproject'
      const config = {headers: {'Content-Type': 'application/json'}}
      const data = {
        '_projectid': project
      }


      this.request.post(endPoint, data, config, (response) => {
        let result = response.data[0].jsonresult
        let listDataviz = []
        for (let i = 0; i < result.length; i++) {
          let item = {
            layerId: result[i].layerid,
            dataViz: result[i].jbData
          }
          listDataviz.push(item)
        }
        resolve(listDataviz)
      }, (error) => {
        console.warn(error)
      })
    })
  }

  /**
   * Get the active project
   *
   * @async
   * @function getOptions
   * @param {number} project - Project Id
   * @param {number} environment - Environment Id
   * @return {Promise<json>} The json with project options
   */
  async getOptions (project, environment) {
    return new Promise((resolve, reject) => {
      if (typeof project === 'undefined') reject(new Error('Esperado o identificador do project'))
      if (typeof environment === 'undefined') reject(new Error('Esperado o identificador do environment'))

      let endPoint = `/api/0.1-a/project/${project}/environment/${environment}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Rename the project
   *
   * @async
   * @function rename
   * @param {object} model - Model of the metadata
   * @return {Promise<json>} The json with project updated
   */
  async rename (model) {
    return new Promise((resolve, reject) => {
      if (typeof model === 'undefined') reject(new Error('Esperado o modelo do projeto'))
      let endPoint = `/api/0.1-a/Project`

      this.request.patch(endPoint, model, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }

  /**
   * Defines the project as active
   *
   * @async
   * @function setActive
   * @param {object} model - Model of the metadata
   * @return {Promise<json>} The json with association updated
   */
  async setActive (model) {
    return new Promise((resolve, reject) => {
      if (typeof model === 'undefined') reject(new Error('Esperado o modelo do projeto'))
      let endPoint = `/api/0.1-a/Project/user`

      this.request.patch(endPoint, model, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }

  /**
   * Defines the project as active
   *
   * @async
   * @function setActive
   * @param {object} model - Model of the metadata
   * @return {Promise<json>} The json with association updated
   */
  async setActiveProjectAndDeactivateOthers (environmentId,userId, projectId) {
    return new Promise((resolve, reject) => {
      let request = new Request({tag: 'python'})
      let endPoint = `/project/${environmentId}/${userId}/${projectId}`
      request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response.data))
        .catch(() => reject)
    })
  }

  /**
   * Update project options
   *
   * @async
   * @function updateOptions
   * @param {object} options - Project options
   * @return {Promise<json>} The json with project options created
   */
  async updateOptions (options = {}) {
    return new Promise((resolve, reject) => {
      if (typeof options === 'undefined') reject(new Error('Esperado o objeto options'))

      const endPoint = `/api/0.1-a/project/options`
      this.request.patch(endPoint, options, defaultJsonHeaders)
        .then(res => resolve(res.data))
        .catch(() => reject)
    })
  }


}

export {Project}
